import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Star, ShieldCheck, BadgeCheck, Lock, ArrowRight, HeartHandshake } from 'lucide-react';
import { MaskedLines } from '../motion';
import QuoteForm from '../QuoteForm';
import { useT } from '../../i18n/LangContext';
import { IMG, BADGES, GOOGLE_PROFILE_URL } from '../../data/content';

export default function Hero() {
  const t = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative overflow-hidden bg-ink">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={IMG.heroKitchen} alt={t['hero.imgAlt']} fetchPriority="high"
          className="photo-treat h-[115%] w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      <div className="hero-glow absolute inset-0" />

      <div className="container-site relative z-10 grid gap-14 pb-28 pt-16 md:pt-24 lg:grid-cols-12 lg:gap-10 lg:pb-36">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="flex flex-wrap items-center gap-4">
            <img data-testid="badge-bbb" src={BADGES.bbb} alt={t['hero.bbb']} className="h-12 w-auto rounded-md shadow-xl md:h-14" />
            <a data-testid="badge-google" href={GOOGLE_PROFILE_URL} target="_blank" rel="noopener noreferrer" aria-label={t['hero.google']}>
              <img src={BADGES.google} alt={t['hero.google']} className="h-12 w-auto rounded-md shadow-xl transition-transform duration-300 hover:scale-[1.04] md:h-14" />
            </a>
          </motion.div>

          <MaskedLines as="h1" delay={0.25} className="mt-8 font-serif text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
            lines={[
              { text: t['hero.l1'], cls: 'text-white' },
              { text: t['hero.l2'], cls: 'italic text-gold' },
              { text: t['hero.l3'], cls: 'text-silver' },
            ]} />

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/75 md:text-lg">
            {t['hero.sub']}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.95 }}
            data-testid="hero-rating" className="mt-6 flex items-center gap-2.5">
            <span className="flex text-gold">{[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-gold" />)}</span>
            <span className="text-lg font-extrabold text-white">4.9</span>
            <span className="text-sm text-silver">{t['hero.reviews']}</span>
          </motion.div>

          <motion.ul initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-6 flex flex-wrap gap-x-7 gap-y-3">
            {[
              [ShieldCheck, t['hero.t1'], 'trust-insured'],
              [BadgeCheck, t['hero.t2'], 'trust-wsib'],
              [Lock, t['hero.t3'], 'trust-bonded'],
            ].map(([Icon, label, tid]) => (
              <li key={tid} data-testid={tid} className="flex items-center gap-2 text-sm font-bold text-white">
                <Icon className="h-5 w-5 text-gold" /> {label}
              </li>
            ))}
          </motion.ul>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-9 flex flex-wrap items-center gap-5">
            <Link data-testid="hero-primary-cta" to="/quote" className="gold-btn text-base !px-9 !py-4">
              {t['hero.cta']} <ArrowRight className="h-5 w-5" />
            </Link>
            <p data-testid="hero-senior-discount" className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2.5 text-sm font-bold text-gold">
              <HeartHandshake className="h-4 w-4" /> {t['hero.senior']}
            </p>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div initial={{ opacity: 0, y: 48 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}>
            <QuoteForm variant="card" source="hero" idPrefix="hero-quote" />
            <div data-testid="canadian-badge" className="mt-6 flex justify-center">
              <img src={BADGES.canadian} alt={t['hero.canada']} className="h-28 w-auto drop-shadow-2xl" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
